import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    FillsChartType,
    FillsGraphConfiguration,
} from './store/configuration/FillsGraphConfiguration';
import { FillsGraphData } from './fillsGraphData';
import OrderFillsChart from './orderFills/OrderFillsChart';
import ExpectedVsActualChart from './expectedVsActual/ExpectedVsActualChart';
import PercentageOfMarketVolumeChart from './percentageOfMarketVolume/PercentageOfMarketVolumeChart';
import PerformanceVsIndexChart from './performanceVsIndex/PerformanceVsIndexChart';
import PLChart from './pl/PLChart';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import { calculateChartHeights } from 'widgets/fillsGraph/dimensions';
import { useLegendStatus } from 'widgets/fillsGraph/legendStatus';
import {
    getIsOrderFillsVisible,
    getLastVisibleChart,
    isCompanionChartVisible,
    isThereACompanionChartVisible,
    useCompanionChartVisibilities,
} from 'widgets/fillsGraph/companionChartVisibility';
import { DockingPanel } from '@instinet-analytics/semantic-ui-components';
import { useDispatchUpdateFillsGraphConfiguration } from 'widgets/fillsGraph/store/configuration/dispatcher';

interface FillsChartsContainerProps {
    fillsGraphPortfolioData: FillsGraphPortfolioData;
    fillsGraphData: FillsGraphData;
    config: FillsGraphConfiguration;
    height: number;
    width: number;
}

const FillsChartsContainer = (props: FillsChartsContainerProps) => {
    const { fillsGraphData, fillsGraphPortfolioData, config, height, width } =
        props;
    const {
        [FillsChartType.ExpectedVsActual]: expectedVsActualConfig,
        [FillsChartType.PercentageOfMarketVolume]:
            percentageOfMarketVolumeConfig,
        [FillsChartType.PnL]: pnlChartConfig,
        general: { barSizeInMinutes },
        dockPercentage,
    } = config;

    const isOrderFillsVisible = getIsOrderFillsVisible(config);

    const dispatchConfigChange = useDispatchUpdateFillsGraphConfiguration();

    const legendStatus = useLegendStatus(
        fillsGraphData,
        fillsGraphPortfolioData
    );

    const companionChartVisibilities = useCompanionChartVisibilities(
        fillsGraphData,
        fillsGraphPortfolioData
    );

    const divRef = useRef<HTMLDivElement>(null);
    const [left, setLeft] = useState(0);
    useEffect(() => {
        if (!divRef.current) {
            return;
        }
        setLeft(divRef.current.getBoundingClientRect().left);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, height, divRef.current]);

    const {
        companionChartsHeight,
        orderFillsHeight,
        expectedVsActualHeight,
        expectedVsActualChartHeight,
        percentageOfMarketVolumeHeight,
        percentageOfMarketVolumeChartHeight,
        plHeight,
        plChartHeight,
        performanceVsIndexHeight,
        performanceVsIndexChartHeight,
    } = calculateChartHeights(
        height,
        companionChartVisibilities,
        isOrderFillsVisible,
        legendStatus,
        dockPercentage
    );

    const lastVisibleChart = getLastVisibleChart(companionChartVisibilities);

    const isLastVisible = (chartType: FillsChartType) =>
        lastVisibleChart === chartType;

    const handlePercentageChange = (nextPercent: number) => {
        dispatchConfigChange({ ...config, dockPercentage: nextPercent });
    };

    const style = useMemo(() => ({ height }), [height]);

    const companionChartsStyle = useMemo(
        () => ({ height: companionChartsHeight }),
        [companionChartsHeight]
    );

    return (
        <div className={`FillsChartsContainer`} ref={divRef} style={style}>
            <DockingPanel
                position={'Bottom'}
                percentage={dockPercentage}
                onPercentageChange={handlePercentageChange}
            >
                {isOrderFillsVisible && (
                    <OrderFillsChart
                        width={width}
                        chartHeight={orderFillsHeight}
                        fillsGraphContainerLeft={left}
                        fillsGraphData={fillsGraphData}
                        fillsGraphPortfolioData={fillsGraphPortfolioData}
                        config={config}
                        displayXAxis={isLastVisible(FillsChartType.OrderFills)}
                    />
                )}
                {isThereACompanionChartVisible(companionChartVisibilities) && (
                    <div
                        className="companion-charts"
                        style={companionChartsStyle}
                    >
                        {isCompanionChartVisible(
                            companionChartVisibilities,
                            FillsChartType.ExpectedVsActual
                        ) && (
                            <ExpectedVsActualChart
                                width={width}
                                height={expectedVsActualHeight}
                                chartHeight={expectedVsActualChartHeight}
                                fillsGraphContainerLeft={left}
                                fillsGraphData={fillsGraphData}
                                fillsGraphPortfolioData={
                                    fillsGraphPortfolioData
                                }
                                config={expectedVsActualConfig}
                                bucketSizeInMs={barSizeInMinutes * 60000}
                                displayXAxis={isLastVisible(
                                    FillsChartType.ExpectedVsActual
                                )}
                            />
                        )}
                        {isCompanionChartVisible(
                            companionChartVisibilities,
                            FillsChartType.PercentageOfMarketVolume
                        ) && (
                            <PercentageOfMarketVolumeChart
                                width={width}
                                height={percentageOfMarketVolumeHeight}
                                chartHeight={
                                    percentageOfMarketVolumeChartHeight
                                }
                                fillsGraphContainerLeft={left}
                                fillsGraphData={fillsGraphData}
                                fillsGraphPortfolioData={
                                    fillsGraphPortfolioData
                                }
                                config={percentageOfMarketVolumeConfig}
                                displayXAxis={isLastVisible(
                                    FillsChartType.PercentageOfMarketVolume
                                )}
                            />
                        )}
                        {isCompanionChartVisible(
                            companionChartVisibilities,
                            FillsChartType.PnL
                        ) && (
                            <PLChart
                                width={width}
                                height={plHeight}
                                chartHeight={plChartHeight}
                                fillsGraphContainerLeft={left}
                                fillsGraphData={fillsGraphData}
                                fillsGraphPortfolioData={
                                    fillsGraphPortfolioData
                                }
                                config={pnlChartConfig}
                                displayXAxis={isLastVisible(FillsChartType.PnL)}
                            />
                        )}
                        {isCompanionChartVisible(
                            companionChartVisibilities,
                            FillsChartType.PerformanceVsIndex
                        ) && (
                            <PerformanceVsIndexChart
                                height={performanceVsIndexHeight}
                                chartHeight={performanceVsIndexChartHeight}
                                fillsGraphContainerLeft={left}
                                fillsGraphData={fillsGraphData}
                                fillsGraphPortfolioData={
                                    fillsGraphPortfolioData
                                }
                                config={config}
                                displayXAxis={isLastVisible(
                                    FillsChartType.PerformanceVsIndex
                                )}
                            />
                        )}
                    </div>
                )}
            </DockingPanel>
        </div>
    );
};

export default FillsChartsContainer;
